/**
 * Landing Page Multilingual Translations
 * Supports 15 popular languages for the official website
 */

export interface LandingTranslation {
    // Navbar
    getStarted: string;

    // Hero Section
    badge: string;
    tagline: string;
    heroTitle1: string;
    heroTitle2: string;
    heroSubtitle: string;
    startFree: string;
    learnMore: string;

    // Stats Section
    jurisdictionsLabel: string;
    accuracyLabel: string;
    fasterLabel: string;
    availableLabel: string;

    // Features Section
    featuresTitle: string;
    featuresSubtitle: string;
    platformCapabilities: string;

    // Feature Cards
    globalCoverage: string;
    globalCoverageDesc: string;
    smartReview: string;
    smartReviewDesc: string;
    autoDrafting: string;
    autoDraftingDesc: string;
    caseSearch: string;
    caseSearchDesc: string;
    enterpriseSecurity: string;
    enterpriseSecurityDesc: string;
    multilingual: string;
    multilingualDesc: string;

    // Testimonial Section
    testimonialQuote: string;
    testimonialAuthor: string;
    testimonialPosition: string;

    // Trust Section
    coveringJurisdictions: string;
    moreCountries: string;

    // Pricing Section
    pricingTitle: string;
    pricingSubtitle: string;
    planFreeDesc: string;
    planProDesc: string;
    planMaxDesc: string;
    planUltraDesc: string;
    perMonth: string;
    chatsPerDay: string;
    kbFiles: string;
    singleCountryCoverage: string;
    singleCountryDeep: string;
    globalCoveragePricing: string;
    multiCountryAnalysis: string;
    aiReviewPerMonth: string;
    deepResearchQuota: string;
    noAiReview: string;
    noDeepResearch: string;
    startUsing: string;
    subscribeNow: string;
    enterpriseCustom: string;
    contactSales: string;
    viewPricing: string;

    // CTA Section
    readyToStart: string;
    joinThousands: string;
    signUpFree: string;

    // Footer
    footerPricing: string;
    footerPrivacy: string;
    footerTerms: string;
    footerContact: string;
    footerCopyright: string;
}

export const LANDING_LANGUAGES = [
    { code: 'en-US', name: 'English', nativeName: 'English' },
    { code: 'zh-CN', name: 'Chinese (Simplified)', nativeName: '简体中文' },
    { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '繁體中文' },
    { code: 'ja', name: 'Japanese', nativeName: '日本語' },
    { code: 'ko', name: 'Korean', nativeName: '한국어' },
    { code: 'de', name: 'German', nativeName: 'Deutsch' },
    { code: 'fr-FR', name: 'French', nativeName: 'Français' },
    { code: 'es-ES', name: 'Spanish', nativeName: 'Español' },
    { code: 'pt-BR', name: 'Portuguese', nativeName: 'Português' },
    { code: 'ru', name: 'Russian', nativeName: 'Русский' },
    { code: 'ar', name: 'Arabic', nativeName: 'العربية' },
    { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी' },
    { code: 'it', name: 'Italian', nativeName: 'Italiano' },
    { code: 'nl', name: 'Dutch', nativeName: 'Nederlands' },
    { code: 'tr', name: 'Turkish', nativeName: 'Türkçe' },
];

export const landingTranslations: Record<string, LandingTranslation> = {
    'en-US': {
        getStarted: 'Get Started',
        badge: 'AI Legal Engine 2.0 Released',
        heroTitle1: 'Legal AI',
        heroTitle2: 'Simplified.',
        heroSubtitle: 'Your intelligent legal assistant. Access 190+ jurisdictions worldwide. From contract review to document drafting, make complex legal work clear and efficient.',
        startFree: 'Start Free',
        learnMore: 'Learn More',
        featuresTitle: 'Reimagine Legal Workflow',
        featuresSubtitle: 'From legal research to document drafting, AI powers every step',
        globalCoverage: 'Global Coverage',
        globalCoverageDesc: 'Access legal systems from 190+ countries. One-stop solution for cross-border business.',
        smartReview: 'Smart Review',
        smartReviewDesc: 'AI-powered contract analysis identifies risks in seconds',
        autoDrafting: 'Auto Drafting',
        autoDraftingDesc: 'Generate professional legal documents automatically',
        caseSearch: 'Case Search',
        caseSearchDesc: 'Intelligent case matching from vast legal databases',
        enterpriseSecurity: 'Enterprise Security',
        enterpriseSecurityDesc: 'End-to-end encryption, SOC2 compliant. Your legal documents stay yours.',
        multilingual: 'Multilingual',
        multilingualDesc: 'Legal consultation in Chinese, English, Japanese & more',
        coveringJurisdictions: 'COVERING MAJOR JURISDICTIONS',
        moreCountries: 'and 180+ more countries',
        // Pricing Section
        pricingTitle: 'Simple, Transparent Pricing',
        pricingSubtitle: 'Choose the plan that fits your needs',
        planFreeDesc: 'Experience basic legal Q&A',
        planProDesc: 'Smart assistant for individual lawyers',
        planMaxDesc: 'Seamless global legal access',
        planUltraDesc: 'Cross-border strategic intelligence',
        perMonth: '/mo',
        chatsPerDay: 'chats/day',
        kbFiles: 'knowledge base files',
        singleCountryCoverage: 'Single country coverage',
        singleCountryDeep: 'Single country deep coverage',
        globalCoveragePricing: 'Global legal coverage',
        multiCountryAnalysis: 'Multi-country comparison',
        aiReviewPerMonth: 'AI reviews/month',
        deepResearchQuota: 'Deep Research',
        noAiReview: 'No AI Review/Draft',
        noDeepResearch: 'No Deep Research',
        startUsing: 'Get Started',
        subscribeNow: 'Subscribe Now',
        enterpriseCustom: 'Need an enterprise solution?',
        contactSales: 'Contact Sales',
        readyToStart: 'Ready to get started?',
        joinThousands: 'Join thousands of legal professionals worldwide',
        signUpFree: 'Sign Up Free',
        tagline: 'AI-Powered Legal Intelligence',
        jurisdictionsLabel: 'Jurisdictions',
        accuracyLabel: 'Accuracy',
        fasterLabel: 'Faster',
        availableLabel: 'Available',
        platformCapabilities: 'Platform Capabilities',
        testimonialQuote: '"LawHelper has transformed how we handle legal work. Contract review is 10x faster, with incredible accuracy."',
        testimonialAuthor: 'Legal Professional',
        testimonialPosition: 'Partner at Top Law Firm',
        viewPricing: 'View Pricing',
        footerPricing: 'Pricing',
        footerPrivacy: 'Privacy',
        footerTerms: 'Terms',
        footerContact: 'Contact',
        footerCopyright: '© 2024 LawHelper. All rights reserved.',
    },

    'zh-CN': {
        getStarted: '开始使用',
        badge: 'AI 法律引擎 2.0 发布',
        heroTitle1: 'Legal AI',
        heroTitle2: '让法律变得简单',
        heroSubtitle: '智能法律助手，覆盖全球 190+ 国家法律体系。从合同审查到文书起草，让复杂的法律工作变得清晰高效。',
        startFree: '免费开始',
        learnMore: '了解更多',
        featuresTitle: '重新定义法律工作流',
        featuresSubtitle: '从法律研究到文书起草，AI 助力每一个环节',
        globalCoverage: '全球法律覆盖',
        globalCoverageDesc: '支持 190+ 国家和地区的法律法规查询与分析，跨境业务一站式解决。',
        smartReview: '智能合同审查',
        smartReviewDesc: 'AI 秒级分析合同风险点，精准识别潜在问题',
        autoDrafting: '文书自动起草',
        autoDraftingDesc: '根据需求自动生成专业法律文书模板',
        caseSearch: '案例智能检索',
        caseSearchDesc: '海量判例库智能匹配，快速定位相关案例',
        enterpriseSecurity: '企业级安全',
        enterpriseSecurityDesc: '端到端加密保护，SOC2 认证，数据安全可控。您的法律文件只属于您。',
        multilingual: '多语言支持',
        multilingualDesc: '支持中英日韩等多语言法律咨询服务',
        coveringJurisdictions: '覆盖全球主要法域',
        moreCountries: '以及 180+ 其他国家和地区',
        // Pricing Section
        pricingTitle: '简单透明的定价',
        pricingSubtitle: '选择适合您需求的方案',
        planFreeDesc: '体验基础法律问答',
        planProDesc: '个人律师的智能助手',
        planMaxDesc: '全球法律无缝访问',
        planUltraDesc: '跨境战略智库级服务',
        perMonth: '/月',
        chatsPerDay: '次对话/天',
        kbFiles: '个知识库文件',
        singleCountryCoverage: '单国家法律覆盖',
        singleCountryDeep: '单国家深度覆盖',
        globalCoveragePricing: '全球法律覆盖',
        multiCountryAnalysis: '多国对比分析',
        aiReviewPerMonth: '次 AI 审查/月',
        deepResearchQuota: '深度研究',
        noAiReview: 'AI 审查/起草',
        noDeepResearch: '深度研究',
        startUsing: '开始使用',
        subscribeNow: '立即订阅',
        enterpriseCustom: '需要企业定制方案？',
        contactSales: '联系销售团队',
        readyToStart: '准备好开始了吗？',
        joinThousands: '加入全球数千名法律专业人士的选择',
        signUpFree: '免费注册',
        tagline: 'AI 驱动的法律智能',
        jurisdictionsLabel: '覆盖国家/地区',
        accuracyLabel: '准确率',
        fasterLabel: '效率提升',
        availableLabel: '全天候服务',
        platformCapabilities: '核心功能',
        testimonialQuote: '"LawHelper 彻底改变了我们的法律工作方式。合同审查效率提升了 10 倍，而且准确率极高。"',
        testimonialAuthor: '李律师',
        testimonialPosition: '某知名律所合伙人',
        viewPricing: '查看定价',
        footerPricing: '定价',
        footerPrivacy: '隐私政策',
        footerTerms: '服务条款',
        footerContact: '联系我们',
        footerCopyright: '© 2024 LawHelper 版权所有',
    },

    'zh-TW': {
        getStarted: '開始使用',
        badge: 'AI 法律引擎 2.0 發布',
        heroTitle1: 'Legal AI',
        heroTitle2: '讓法律變得簡單',
        heroSubtitle: '智能法律助手，覆蓋全球 190+ 國家法律體系。從合同審查到文書起草，讓複雜的法律工作變得清晰高效。',
        startFree: '免費開始',
        learnMore: '了解更多',
        featuresTitle: '重新定義法律工作流',
        featuresSubtitle: '從法律研究到文書起草，AI 助力每一個環節',
        globalCoverage: '全球法律覆蓋',
        globalCoverageDesc: '支持 190+ 國家和地區的法律法規查詢與分析，跨境業務一站式解決。',
        smartReview: '智能合同審查',
        smartReviewDesc: 'AI 秒級分析合同風險點，精準識別潛在問題',
        autoDrafting: '文書自動起草',
        autoDraftingDesc: '根據需求自動生成專業法律文書模板',
        caseSearch: '案例智能檢索',
        caseSearchDesc: '海量判例庫智能匹配，快速定位相關案例',
        enterpriseSecurity: '企業級安全',
        enterpriseSecurityDesc: '端到端加密保護，SOC2 認證，數據安全可控。您的法律文件只屬於您。',
        multilingual: '多語言支持',
        multilingualDesc: '支持中英日韓等多語言法律諮詢服務',
        coveringJurisdictions: '覆蓋全球主要法域',
        moreCountries: '以及 180+ 其他國家和地區',
        // Pricing Section
        pricingTitle: '簡單透明的定價',
        pricingSubtitle: '選擇適合您需求的方案',
        planFreeDesc: '體驗基礎法律問答',
        planProDesc: '個人律師的智能助手',
        planMaxDesc: '全球法律無縫訪問',
        planUltraDesc: '跨境戰略智庫級服務',
        perMonth: '/月',
        chatsPerDay: '次對話/天',
        kbFiles: '個知識庫文件',
        singleCountryCoverage: '單國家法律覆蓋',
        singleCountryDeep: '單國家深度覆蓋',
        globalCoveragePricing: '全球法律覆蓋',
        multiCountryAnalysis: '多國對比分析',
        aiReviewPerMonth: '次 AI 審查/月',
        deepResearchQuota: '深度研究',
        noAiReview: 'AI 審查/起草',
        noDeepResearch: '深度研究',
        startUsing: '開始使用',
        subscribeNow: '立即訂閱',
        enterpriseCustom: '需要企業定制方案？',
        contactSales: '聯繫銷售團隊',
        readyToStart: '準備好開始了嗎？',
        joinThousands: '加入全球數千名法律專業人士的選擇',
        signUpFree: '免費註冊',
        tagline: 'AI 驅動的法律智能',
        jurisdictionsLabel: '覆蓋國家/地區',
        accuracyLabel: '準確率',
        fasterLabel: '效率提升',
        availableLabel: '全天候服務',
        platformCapabilities: '核心功能',
        testimonialQuote: '"LawHelper 徹底改變了我們的法律工作方式。合同審查效率提升了 10 倍，而且準確率極高。"',
        testimonialAuthor: '李律師',
        testimonialPosition: '某知名律所合夥人',
        viewPricing: '查看定價',
        footerPricing: '定價',
        footerPrivacy: '隱私政策',
        footerTerms: '服務條款',
        footerContact: '聯繫我們',
        footerCopyright: '© 2024 LawHelper 版權所有',
    },

    'ja': {
        getStarted: '始める',
        badge: 'AI法務エンジン2.0リリース',
        heroTitle1: 'Legal AI',
        heroTitle2: 'シンプルに。',
        heroSubtitle: 'インテリジェント法務アシスタント。世界190以上の法域にアクセス。契約レビューから文書作成まで、複雑な法務作業を明確かつ効率的に。',
        startFree: '無料で始める',
        learnMore: '詳細を見る',
        featuresTitle: '法務ワークフローを再定義',
        featuresSubtitle: '法務リサーチから文書作成まで、AIがすべてのステップをサポート',
        globalCoverage: 'グローバルカバレッジ',
        globalCoverageDesc: '190以上の国の法律システムにアクセス。クロスボーダービジネスのワンストップソリューション。',
        smartReview: 'スマートレビュー',
        smartReviewDesc: 'AIによる契約分析で数秒でリスクを特定',
        autoDrafting: '自動ドラフト',
        autoDraftingDesc: 'プロフェッショナルな法務文書を自動生成',
        caseSearch: '判例検索',
        caseSearchDesc: '膨大な法務データベースからインテリジェントな判例マッチング',
        enterpriseSecurity: 'エンタープライズセキュリティ',
        enterpriseSecurityDesc: 'エンドツーエンド暗号化、SOC2準拠。あなたの法務文書は安全です。',
        multilingual: '多言語対応',
        multilingualDesc: '中国語、英語、日本語などで法務相談',
        coveringJurisdictions: '主要法域をカバー',
        moreCountries: 'およびその他180以上の国',
        // Pricing Section
        pricingTitle: 'シンプルで透明な料金',
        pricingSubtitle: 'ニーズに合ったプランを選択',
        planFreeDesc: '基本的な法律Q&Aを体験',
        planProDesc: '個人弁護士向けスマートアシスタント',
        planMaxDesc: 'グローバル法律へのシームレスなアクセス',
        planUltraDesc: '国境を越えた戦略的インテリジェンス',
        perMonth: '/月',
        chatsPerDay: 'チャット/日',
        kbFiles: 'ナレッジベースファイル',
        singleCountryCoverage: '単一国カバレッジ',
        singleCountryDeep: '単一国深層カバレッジ',
        globalCoveragePricing: 'グローバル法律カバレッジ',
        multiCountryAnalysis: '多国間比較分析',
        aiReviewPerMonth: 'AIレビュー/月',
        deepResearchQuota: '深層リサーチ',
        noAiReview: 'AIレビュー/ドラフトなし',
        noDeepResearch: '深層リサーチなし',
        startUsing: '始める',
        subscribeNow: '今すぐ購読',
        enterpriseCustom: 'エンタープライズソリューションが必要ですか？',
        contactSales: '営業に連絡',
        readyToStart: '始める準備はできましたか？',
        joinThousands: '世界中の数千人の法務専門家と共に',
        signUpFree: '無料登録',
        tagline: 'AI駆動の法務インテリジェンス',
        jurisdictionsLabel: '対応法域',
        accuracyLabel: '精度',
        fasterLabel: '高速化',
        availableLabel: '24時間対応',
        platformCapabilities: 'プラットフォーム機能',
        testimonialQuote: '"LawHelperは法務業務の進め方を一変させました。契約レビューが10倍速くなり、驚くべき精度です。"',
        testimonialAuthor: '法務専門家',
        testimonialPosition: '大手法律事務所パートナー',
        viewPricing: '料金を見る',
        footerPricing: '料金',
        footerPrivacy: 'プライバシー',
        footerTerms: '利用規約',
        footerContact: 'お問い合わせ',
        footerCopyright: '© 2024 LawHelper. All rights reserved.',
    },

    'ko': {
        getStarted: '시작하기',
        badge: 'AI 법률 엔진 2.0 출시',
        heroTitle1: 'Legal AI',
        heroTitle2: '간편하게.',
        heroSubtitle: '지능형 법률 어시스턴트. 전 세계 190개 이상의 관할권에 접근. 계약 검토부터 문서 작성까지, 복잡한 법률 업무를 명확하고 효율적으로.',
        startFree: '무료로 시작',
        learnMore: '자세히 보기',
        featuresTitle: '법률 워크플로우 재정의',
        featuresSubtitle: '법률 연구부터 문서 작성까지, AI가 모든 단계를 지원',
        globalCoverage: '글로벌 커버리지',
        globalCoverageDesc: '190개 이상 국가의 법률 시스템에 접근. 국경을 초월한 비즈니스를 위한 원스톱 솔루션.',
        smartReview: '스마트 리뷰',
        smartReviewDesc: 'AI 기반 계약 분석으로 몇 초 만에 리스크 식별',
        autoDrafting: '자동 초안 작성',
        autoDraftingDesc: '전문 법률 문서를 자동으로 생성',
        caseSearch: '판례 검색',
        caseSearchDesc: '방대한 법률 데이터베이스에서 지능형 판례 매칭',
        enterpriseSecurity: '엔터프라이즈 보안',
        enterpriseSecurityDesc: '종단간 암호화, SOC2 준수. 귀하의 법률 문서는 안전합니다.',
        multilingual: '다국어 지원',
        multilingualDesc: '중국어, 영어, 일본어 등으로 법률 상담',
        coveringJurisdictions: '주요 관할권 커버',
        moreCountries: '및 180개 이상의 국가',
        // Pricing Section
        pricingTitle: '간단하고 투명한 가격',
        pricingSubtitle: '필요에 맞는 플랜 선택',
        planFreeDesc: '기본 법률 Q&A 체험',
        planProDesc: '개인 변호사를 위한 스마트 어시스턴트',
        planMaxDesc: '글로벌 법률에 대한 원활한 접근',
        planUltraDesc: '국경을 초월한 전략적 인텔리전스',
        perMonth: '/월',
        chatsPerDay: '채팅/일',
        kbFiles: '지식 베이스 파일',
        singleCountryCoverage: '단일 국가 커버리지',
        singleCountryDeep: '단일 국가 심층 커버리지',
        globalCoveragePricing: '글로벌 법률 커버리지',
        multiCountryAnalysis: '다국가 비교 분석',
        aiReviewPerMonth: 'AI 리뷰/월',
        deepResearchQuota: '심층 연구',
        noAiReview: 'AI 리뷰/초안 없음',
        noDeepResearch: '심층 연구 없음',
        startUsing: '시작하기',
        subscribeNow: '지금 구독',
        enterpriseCustom: '엔터프라이즈 솔루션이 필요하신가요?',
        contactSales: '영업팀 연락',
        readyToStart: '시작할 준비가 되셨나요?',
        joinThousands: '전 세계 수천 명의 법률 전문가와 함께하세요',
        signUpFree: '무료 가입',
        tagline: 'AI 기반 법률 인텔리전스',
        jurisdictionsLabel: '관할권',
        accuracyLabel: '정확도',
        fasterLabel: '빠름',
        availableLabel: '이용 가능',
        platformCapabilities: '플랫폼 기능',
        testimonialQuote: '"LawHelper는 법률 업무 처리 방식을 완전히 바꿔놓았습니다. 계약 검토가 10배 빨라졌고 놀라운 정확도를 보여줍니다."',
        testimonialAuthor: '법률 전문가',
        testimonialPosition: '대형 로펌 파트너',
        viewPricing: '가격 보기',
        footerPricing: '가격',
        footerPrivacy: '개인정보 보호',
        footerTerms: '이용약관',
        footerContact: '문의',
        footerCopyright: '© 2024 LawHelper. All rights reserved.',
    },

    'de': {
        getStarted: 'Loslegen',
        badge: 'AI Legal Engine 2.0 veröffentlicht',
        heroTitle1: 'Legal AI',
        heroTitle2: 'Vereinfacht.',
        heroSubtitle: 'Ihr intelligenter Rechtsassistent. Zugang zu über 190 Jurisdiktionen weltweit. Von der Vertragsprüfung bis zur Dokumentenerstellung – komplexe Rechtsarbeit klar und effizient.',
        startFree: 'Kostenlos starten',
        learnMore: 'Mehr erfahren',
        featuresTitle: 'Rechtliche Workflows neu gedacht',
        featuresSubtitle: 'Von der Rechtsrecherche bis zur Dokumentenerstellung – KI unterstützt jeden Schritt',
        globalCoverage: 'Globale Abdeckung',
        globalCoverageDesc: 'Zugang zu Rechtssystemen aus über 190 Ländern. One-Stop-Lösung für grenzüberschreitende Geschäfte.',
        smartReview: 'Smart Review',
        smartReviewDesc: 'KI-gestützte Vertragsanalyse identifiziert Risiken in Sekunden',
        autoDrafting: 'Auto-Entwurf',
        autoDraftingDesc: 'Professionelle Rechtsdokumente automatisch erstellen',
        caseSearch: 'Fallsuche',
        caseSearchDesc: 'Intelligentes Fall-Matching aus umfangreichen Rechtsdatenbanken',
        enterpriseSecurity: 'Enterprise-Sicherheit',
        enterpriseSecurityDesc: 'Ende-zu-Ende-Verschlüsselung, SOC2-konform. Ihre Rechtsdokumente bleiben Ihre.',
        multilingual: 'Mehrsprachig',
        multilingualDesc: 'Rechtsberatung in Chinesisch, Englisch, Japanisch und mehr',
        coveringJurisdictions: 'ABDECKUNG WICHTIGER JURISDIKTIONEN',
        moreCountries: 'und 180+ weitere Länder',
        // Pricing Section
        pricingTitle: 'Einfache, transparente Preise',
        pricingSubtitle: 'Wählen Sie den Plan, der zu Ihnen passt',
        planFreeDesc: 'Erleben Sie grundlegende Rechts-Q&A',
        planProDesc: 'Smart Assistant für Einzelanwälte',
        planMaxDesc: 'Nahtloser globaler Rechtszugang',
        planUltraDesc: 'Grenzüberschreitende strategische Intelligenz',
        perMonth: '/Monat',
        chatsPerDay: 'Chats/Tag',
        kbFiles: 'Wissensdatenbank-Dateien',
        singleCountryCoverage: 'Einzellandabdeckung',
        singleCountryDeep: 'Einzelland-Tiefenabdeckung',
        globalCoveragePricing: 'Globale Rechtsabdeckung',
        multiCountryAnalysis: 'Mehrländervergleich',
        aiReviewPerMonth: 'AI-Reviews/Monat',
        deepResearchQuota: 'Tiefenforschung',
        noAiReview: 'Kein AI-Review/Entwurf',
        noDeepResearch: 'Keine Tiefenforschung',
        startUsing: 'Loslegen',
        subscribeNow: 'Jetzt abonnieren',
        enterpriseCustom: 'Benötigen Sie eine Enterprise-Lösung?',
        contactSales: 'Vertrieb kontaktieren',
        readyToStart: 'Bereit loszulegen?',
        joinThousands: 'Schließen Sie sich Tausenden von Rechtsexperten weltweit an',
        signUpFree: 'Kostenlos registrieren',
        tagline: 'KI-gestützte Rechtsintelligenz',
        jurisdictionsLabel: 'Rechtsgebiete',
        accuracyLabel: 'Genauigkeit',
        fasterLabel: 'Schneller',
        availableLabel: 'Verfügbar',
        platformCapabilities: 'Plattformfunktionen',
        testimonialQuote: '"LawHelper hat unsere Rechtsarbeit revolutioniert. Vertragsprüfung ist 10x schneller, mit unglaublicher Genauigkeit."',
        testimonialAuthor: 'Rechtsexperte',
        testimonialPosition: 'Partner bei führender Kanzlei',
        viewPricing: 'Preise ansehen',
        footerPricing: 'Preise',
        footerPrivacy: 'Datenschutz',
        footerTerms: 'AGB',
        footerContact: 'Kontakt',
        footerCopyright: '© 2024 LawHelper. Alle Rechte vorbehalten.',
    },

    'fr-FR': {
        getStarted: 'Commencer',
        badge: 'AI Legal Engine 2.0 publié',
        heroTitle1: 'Legal AI',
        heroTitle2: 'Simplifié.',
        heroSubtitle: 'Votre assistant juridique intelligent. Accédez à plus de 190 juridictions dans le monde. De la révision de contrats à la rédaction de documents, rendez le travail juridique complexe clair et efficace.',
        startFree: 'Commencer gratuitement',
        learnMore: 'En savoir plus',
        featuresTitle: 'Réinventez le flux de travail juridique',
        featuresSubtitle: 'De la recherche juridique à la rédaction de documents, l\'IA alimente chaque étape',
        globalCoverage: 'Couverture mondiale',
        globalCoverageDesc: 'Accédez aux systèmes juridiques de plus de 190 pays. Solution unique pour les affaires transfrontalières.',
        smartReview: 'Révision intelligente',
        smartReviewDesc: 'L\'analyse de contrats par IA identifie les risques en quelques secondes',
        autoDrafting: 'Rédaction automatique',
        autoDraftingDesc: 'Générez automatiquement des documents juridiques professionnels',
        caseSearch: 'Recherche de cas',
        caseSearchDesc: 'Correspondance intelligente de cas à partir de vastes bases de données juridiques',
        enterpriseSecurity: 'Sécurité entreprise',
        enterpriseSecurityDesc: 'Chiffrement de bout en bout, conforme SOC2. Vos documents juridiques restent les vôtres.',
        multilingual: 'Multilingue',
        multilingualDesc: 'Consultation juridique en chinois, anglais, japonais et plus',
        coveringJurisdictions: 'COUVRANT LES PRINCIPALES JURIDICTIONS',
        moreCountries: 'et plus de 180 autres pays',
        // Pricing Section
        pricingTitle: 'Tarification simple et transparente',
        pricingSubtitle: 'Choisissez le plan qui vous convient',
        planFreeDesc: 'Découvrez les Q&R juridiques de base',
        planProDesc: 'Assistant intelligent pour avocats individuels',
        planMaxDesc: 'Accès juridique mondial sans couture',
        planUltraDesc: 'Intelligence stratégique transfrontalière',
        perMonth: '/mois',
        chatsPerDay: 'chats/jour',
        kbFiles: 'fichiers de base de connaissances',
        singleCountryCoverage: 'Couverture pays unique',
        singleCountryDeep: 'Couverture approfondie pays unique',
        globalCoveragePricing: 'Couverture juridique mondiale',
        multiCountryAnalysis: 'Analyse multi-pays',
        aiReviewPerMonth: 'révisions IA/mois',
        deepResearchQuota: 'Recherche approfondie',
        noAiReview: 'Pas de révision/brouillon IA',
        noDeepResearch: 'Pas de recherche approfondie',
        startUsing: 'Commencer',
        subscribeNow: 'S\'abonner maintenant',
        enterpriseCustom: 'Besoin d\'une solution entreprise?',
        contactSales: 'Contacter les ventes',
        readyToStart: 'Prêt à commencer?',
        joinThousands: 'Rejoignez des milliers de professionnels du droit dans le monde',
        signUpFree: 'Inscription gratuite',
        tagline: 'Intelligence juridique propulsée par l\'IA',
        jurisdictionsLabel: 'Juridictions',
        accuracyLabel: 'Précision',
        fasterLabel: 'Plus rapide',
        availableLabel: 'Disponible',
        platformCapabilities: 'Capacités de la plateforme',
        testimonialQuote: '"LawHelper a transformé notre façon de gérer le travail juridique. La révision des contrats est 10x plus rapide, avec une précision incroyable."',
        testimonialAuthor: 'Professionnel du droit',
        testimonialPosition: 'Associé dans un cabinet de premier plan',
        viewPricing: 'Voir les tarifs',
        footerPricing: 'Tarifs',
        footerPrivacy: 'Confidentialité',
        footerTerms: 'Conditions',
        footerContact: 'Contact',
        footerCopyright: '© 2024 LawHelper. Tous droits réservés.',
    },

    'es-ES': {
        getStarted: 'Comenzar',
        badge: 'AI Legal Engine 2.0 lanzado',
        heroTitle1: 'Legal AI',
        heroTitle2: 'Simplificado.',
        heroSubtitle: 'Tu asistente legal inteligente. Accede a más de 190 jurisdicciones en todo el mundo. Desde la revisión de contratos hasta la redacción de documentos, haz que el trabajo legal complejo sea claro y eficiente.',
        startFree: 'Comenzar gratis',
        learnMore: 'Saber más',
        featuresTitle: 'Reimagina el flujo de trabajo legal',
        featuresSubtitle: 'Desde la investigación legal hasta la redacción de documentos, la IA impulsa cada paso',
        globalCoverage: 'Cobertura global',
        globalCoverageDesc: 'Accede a sistemas legales de más de 190 países. Solución integral para negocios transfronterizos.',
        smartReview: 'Revisión inteligente',
        smartReviewDesc: 'El análisis de contratos con IA identifica riesgos en segundos',
        autoDrafting: 'Redacción automática',
        autoDraftingDesc: 'Genera documentos legales profesionales automáticamente',
        caseSearch: 'Búsqueda de casos',
        caseSearchDesc: 'Coincidencia inteligente de casos de vastas bases de datos legales',
        enterpriseSecurity: 'Seguridad empresarial',
        enterpriseSecurityDesc: 'Cifrado de extremo a extremo, compatible con SOC2. Tus documentos legales siguen siendo tuyos.',
        multilingual: 'Multilingüe',
        multilingualDesc: 'Consulta legal en chino, inglés, japonés y más',
        coveringJurisdictions: 'CUBRIENDO PRINCIPALES JURISDICCIONES',
        moreCountries: 'y más de 180 países',
        // Pricing Section
        pricingTitle: 'Precios simples y transparentes',
        pricingSubtitle: 'Elige el plan que se adapte a tus necesidades',
        planFreeDesc: 'Experimenta preguntas y respuestas legales básicas',
        planProDesc: 'Asistente inteligente para abogados individuales',
        planMaxDesc: 'Acceso legal global sin interrupciones',
        planUltraDesc: 'Inteligencia estratégica transfronteriza',
        perMonth: '/mes',
        chatsPerDay: 'chats/día',
        kbFiles: 'archivos de base de conocimientos',
        singleCountryCoverage: 'Cobertura de país único',
        singleCountryDeep: 'Cobertura profunda de país único',
        globalCoveragePricing: 'Cobertura legal global',
        multiCountryAnalysis: 'Análisis multi-país',
        aiReviewPerMonth: 'revisiones IA/mes',
        deepResearchQuota: 'Investigación profunda',
        noAiReview: 'Sin revisión/borrador IA',
        noDeepResearch: 'Sin investigación profunda',
        startUsing: 'Comenzar',
        subscribeNow: 'Suscribirse ahora',
        enterpriseCustom: '¿Necesitas una solución empresarial?',
        contactSales: 'Contactar ventas',
        readyToStart: '¿Listo para comenzar?',
        joinThousands: 'Únete a miles de profesionales legales en todo el mundo',
        signUpFree: 'Registrarse gratis',
        tagline: 'Inteligencia legal impulsada por IA',
        jurisdictionsLabel: 'Jurisdicciones',
        accuracyLabel: 'Precisión',
        fasterLabel: 'Más rápido',
        availableLabel: 'Disponible',
        platformCapabilities: 'Capacidades de la plataforma',
        testimonialQuote: '"LawHelper ha transformado cómo manejamos el trabajo legal. La revisión de contratos es 10x más rápida, con una precisión increíble."',
        testimonialAuthor: 'Profesional legal',
        testimonialPosition: 'Socio en bufete de primer nivel',
        viewPricing: 'Ver precios',
        footerPricing: 'Precios',
        footerPrivacy: 'Privacidad',
        footerTerms: 'Términos',
        footerContact: 'Contacto',
        footerCopyright: '© 2024 LawHelper. Todos los derechos reservados.',
    },

    'pt-BR': {
        getStarted: 'Começar',
        badge: 'AI Legal Engine 2.0 lançado',
        heroTitle1: 'Legal AI',
        heroTitle2: 'Simplificado.',
        heroSubtitle: 'Seu assistente jurídico inteligente. Acesse mais de 190 jurisdições em todo o mundo. Da revisão de contratos à elaboração de documentos, torne o trabalho jurídico complexo claro e eficiente.',
        startFree: 'Começar grátis',
        learnMore: 'Saiba mais',
        featuresTitle: 'Reimagine o fluxo de trabalho jurídico',
        featuresSubtitle: 'Da pesquisa jurídica à elaboração de documentos, a IA impulsiona cada etapa',
        globalCoverage: 'Cobertura global',
        globalCoverageDesc: 'Acesse sistemas jurídicos de mais de 190 países. Solução única para negócios transfronteiriços.',
        smartReview: 'Revisão inteligente',
        smartReviewDesc: 'Análise de contratos com IA identifica riscos em segundos',
        autoDrafting: 'Redação automática',
        autoDraftingDesc: 'Gere documentos jurídicos profissionais automaticamente',
        caseSearch: 'Busca de casos',
        caseSearchDesc: 'Correspondência inteligente de casos de vastos bancos de dados jurídicos',
        enterpriseSecurity: 'Segurança empresarial',
        enterpriseSecurityDesc: 'Criptografia de ponta a ponta, compatível com SOC2. Seus documentos jurídicos permanecem seus.',
        multilingual: 'Multilíngue',
        multilingualDesc: 'Consultoria jurídica em chinês, inglês, japonês e mais',
        coveringJurisdictions: 'COBRINDO PRINCIPAIS JURISDIÇÕES',
        moreCountries: 'e mais de 180 países',
        // Pricing Section
        pricingTitle: 'Preços simples e transparentes',
        pricingSubtitle: 'Escolha o plano que atende às suas necessidades',
        planFreeDesc: 'Experimente perguntas e respostas jurídicas básicas',
        planProDesc: 'Assistente inteligente para advogados individuais',
        planMaxDesc: 'Acesso jurídico global contínuo',
        planUltraDesc: 'Inteligência estratégica transfronteiriça',
        perMonth: '/mês',
        chatsPerDay: 'chats/dia',
        kbFiles: 'arquivos de base de conhecimento',
        singleCountryCoverage: 'Cobertura de país único',
        singleCountryDeep: 'Cobertura profunda de país único',
        globalCoveragePricing: 'Cobertura jurídica global',
        multiCountryAnalysis: 'Análise multi-país',
        aiReviewPerMonth: 'revisões IA/mês',
        deepResearchQuota: 'Pesquisa profunda',
        noAiReview: 'Sem revisão/rascunho IA',
        noDeepResearch: 'Sem pesquisa profunda',
        startUsing: 'Começar',
        subscribeNow: 'Assinar agora',
        enterpriseCustom: 'Precisa de uma solução empresarial?',
        contactSales: 'Contatar vendas',
        readyToStart: 'Pronto para começar?',
        joinThousands: 'Junte-se a milhares de profissionais jurídicos em todo o mundo',
        signUpFree: 'Cadastre-se grátis',
        tagline: 'Inteligência jurídica impulsionada por IA',
        jurisdictionsLabel: 'Jurisdições',
        accuracyLabel: 'Precisão',
        fasterLabel: 'Mais rápido',
        availableLabel: 'Disponível',
        platformCapabilities: 'Capacidades da plataforma',
        testimonialQuote: '"O LawHelper transformou a forma como lidamos com o trabalho jurídico. A revisão de contratos é 10x mais rápida, com precisão incrível."',
        testimonialAuthor: 'Profissional jurídico',
        testimonialPosition: 'Sócio em escritório de primeira linha',
        viewPricing: 'Ver preços',
        footerPricing: 'Preços',
        footerPrivacy: 'Privacidade',
        footerTerms: 'Termos',
        footerContact: 'Contato',
        footerCopyright: '© 2024 LawHelper. Todos os direitos reservados.',
    },

    'ru': {
        getStarted: 'Начать',
        badge: 'AI Legal Engine 2.0 выпущен',
        heroTitle1: 'Legal AI',
        heroTitle2: 'Упрощённо.',
        heroSubtitle: 'Ваш интеллектуальный юридический помощник. Доступ к более чем 190 юрисдикциям по всему миру. От проверки контрактов до составления документов — сделайте сложную юридическую работу ясной и эффективной.',
        startFree: 'Начать бесплатно',
        learnMore: 'Узнать больше',
        featuresTitle: 'Переосмыслите юридический рабочий процесс',
        featuresSubtitle: 'От юридических исследований до составления документов — ИИ поддерживает каждый шаг',
        globalCoverage: 'Глобальный охват',
        globalCoverageDesc: 'Доступ к правовым системам более чем 190 стран. Комплексное решение для трансграничного бизнеса.',
        smartReview: 'Умный анализ',
        smartReviewDesc: 'ИИ-анализ контрактов выявляет риски за секунды',
        autoDrafting: 'Автоматическое составление',
        autoDraftingDesc: 'Автоматическое создание профессиональных юридических документов',
        caseSearch: 'Поиск дел',
        caseSearchDesc: 'Интеллектуальный подбор дел из обширных юридических баз данных',
        enterpriseSecurity: 'Корпоративная безопасность',
        enterpriseSecurityDesc: 'Сквозное шифрование, соответствие SOC2. Ваши юридические документы остаются вашими.',
        multilingual: 'Многоязычность',
        multilingualDesc: 'Юридические консультации на китайском, английском, японском и других языках',
        coveringJurisdictions: 'ОХВАТ ОСНОВНЫХ ЮРИСДИКЦИЙ',
        moreCountries: 'и ещё более 180 стран',
        // Pricing Section
        pricingTitle: 'Простые и прозрачные цены',
        pricingSubtitle: 'Выберите план, который вам подходит',
        planFreeDesc: 'Попробуйте базовые юридические вопросы и ответы',
        planProDesc: 'Умный помощник для индивидуальных юристов',
        planMaxDesc: 'Бесшовный глобальный юридический доступ',
        planUltraDesc: 'Трансграничная стратегическая разведка',
        perMonth: '/мес',
        chatsPerDay: 'чатов/день',
        kbFiles: 'файлов базы знаний',
        singleCountryCoverage: 'Покрытие одной страны',
        singleCountryDeep: 'Глубокое покрытие одной страны',
        globalCoveragePricing: 'Глобальное юридическое покрытие',
        multiCountryAnalysis: 'Многострановой анализ',
        aiReviewPerMonth: 'AI-ревью/мес',
        deepResearchQuota: 'Глубокое исследование',
        noAiReview: 'Без AI-ревью/черновика',
        noDeepResearch: 'Без глубокого исследования',
        startUsing: 'Начать',
        subscribeNow: 'Подписаться сейчас',
        enterpriseCustom: 'Нужно корпоративное решение?',
        contactSales: 'Связаться с продажами',
        readyToStart: 'Готовы начать?',
        joinThousands: 'Присоединяйтесь к тысячам юридических специалистов по всему миру',
        signUpFree: 'Зарегистрироваться бесплатно',
        tagline: 'Юридический интеллект на базе ИИ',
        jurisdictionsLabel: 'Юрисдикции',
        accuracyLabel: 'Точность',
        fasterLabel: 'Быстрее',
        availableLabel: 'Доступно',
        platformCapabilities: 'Возможности платформы',
        testimonialQuote: '"LawHelper полностью изменил наш подход к юридической работе. Проверка контрактов стала в 10 раз быстрее с невероятной точностью."',
        testimonialAuthor: 'Юридический специалист',
        testimonialPosition: 'Партнёр ведущей юридической фирмы',
        viewPricing: 'Посмотреть цены',
        footerPricing: 'Цены',
        footerPrivacy: 'Конфиденциальность',
        footerTerms: 'Условия',
        footerContact: 'Контакты',
        footerCopyright: '© 2024 LawHelper. Все права защищены.',
    },

    'ar': {
        getStarted: 'ابدأ الآن',
        badge: 'تم إطلاق محرك AI القانوني 2.0',
        heroTitle1: 'Legal AI',
        heroTitle2: 'مبسّط.',
        heroSubtitle: 'مساعدك القانوني الذكي. الوصول إلى أكثر من 190 ولاية قضائية حول العالم. من مراجعة العقود إلى صياغة المستندات، اجعل العمل القانوني المعقد واضحًا وفعالًا.',
        startFree: 'ابدأ مجانًا',
        learnMore: 'اعرف المزيد',
        featuresTitle: 'أعد تصور سير العمل القانوني',
        featuresSubtitle: 'من البحث القانوني إلى صياغة المستندات، الذكاء الاصطناعي يدعم كل خطوة',
        globalCoverage: 'تغطية عالمية',
        globalCoverageDesc: 'الوصول إلى الأنظمة القانونية من أكثر من 190 دولة. حل شامل للأعمال العابرة للحدود.',
        smartReview: 'مراجعة ذكية',
        smartReviewDesc: 'يحدد تحليل العقود بالذكاء الاصطناعي المخاطر في ثوانٍ',
        autoDrafting: 'صياغة تلقائية',
        autoDraftingDesc: 'إنشاء مستندات قانونية احترافية تلقائيًا',
        caseSearch: 'بحث القضايا',
        caseSearchDesc: 'مطابقة ذكية للقضايا من قواعد بيانات قانونية واسعة',
        enterpriseSecurity: 'أمان المؤسسات',
        enterpriseSecurityDesc: 'تشفير من طرف إلى طرف، متوافق مع SOC2. مستنداتك القانونية تبقى ملكك.',
        multilingual: 'متعدد اللغات',
        multilingualDesc: 'استشارات قانونية بالصينية والإنجليزية واليابانية والمزيد',
        coveringJurisdictions: 'تغطية الولايات القضائية الرئيسية',
        moreCountries: 'وأكثر من 180 دولة أخرى',
        // Pricing Section
        pricingTitle: 'أسعار بسيطة وشفافة',
        pricingSubtitle: 'اختر الخطة التي تناسب احتياجاتك',
        planFreeDesc: 'جرب الأسئلة والأجوبة القانونية الأساسية',
        planProDesc: 'مساعد ذكي للمحامين الأفراد',
        planMaxDesc: 'وصول قانوني عالمي سلس',
        planUltraDesc: 'استخبارات استراتيجية عابرة للحدود',
        perMonth: '/شهر',
        chatsPerDay: 'محادثات/يوم',
        kbFiles: 'ملفات قاعدة المعرفة',
        singleCountryCoverage: 'تغطية دولة واحدة',
        singleCountryDeep: 'تغطية عميقة لدولة واحدة',
        globalCoveragePricing: 'تغطية قانونية عالمية',
        multiCountryAnalysis: 'تحليل متعدد الدول',
        aiReviewPerMonth: 'مراجعات AI/شهر',
        deepResearchQuota: 'بحث معمق',
        noAiReview: 'بدون مراجعة/مسودة AI',
        noDeepResearch: 'بدون بحث معمق',
        startUsing: 'ابدأ الآن',
        subscribeNow: 'اشترك الآن',
        enterpriseCustom: 'هل تحتاج حلاً مؤسسياً؟',
        contactSales: 'اتصل بالمبيعات',
        readyToStart: 'مستعد للبدء؟',
        joinThousands: 'انضم إلى آلاف المحترفين القانونيين حول العالم',
        signUpFree: 'سجّل مجانًا',
        tagline: 'ذكاء قانوني مدعوم بالذكاء الاصطناعي',
        jurisdictionsLabel: 'الولايات القضائية',
        accuracyLabel: 'الدقة',
        fasterLabel: 'أسرع',
        availableLabel: 'متاح',
        platformCapabilities: 'قدرات المنصة',
        testimonialQuote: '"لقد غيّر LawHelper طريقة تعاملنا مع العمل القانوني. مراجعة العقود أصبحت أسرع 10 مرات، بدقة لا تصدق."',
        testimonialAuthor: 'متخصص قانوني',
        testimonialPosition: 'شريك في مكتب محاماة رائد',
        viewPricing: 'عرض الأسعار',
        footerPricing: 'الأسعار',
        footerPrivacy: 'الخصوصية',
        footerTerms: 'الشروط',
        footerContact: 'اتصل بنا',
        footerCopyright: '© 2024 LawHelper. جميع الحقوق محفوظة.',
    },

    'hi': {
        getStarted: 'शुरू करें',
        badge: 'AI लीगल इंजन 2.0 जारी',
        heroTitle1: 'Legal AI',
        heroTitle2: 'सरल बनाया।',
        heroSubtitle: 'आपका बुद्धिमान कानूनी सहायक। दुनिया भर में 190+ न्यायाधिकार क्षेत्रों तक पहुंच। अनुबंध समीक्षा से लेकर दस्तावेज़ प्रारूपण तक, जटिल कानूनी कार्य को स्पष्ट और कुशल बनाएं।',
        startFree: 'मुफ्त शुरू करें',
        learnMore: 'और जानें',
        featuresTitle: 'कानूनी वर्कफ़्लो को पुनर्परिभाषित करें',
        featuresSubtitle: 'कानूनी शोध से लेकर दस्तावेज़ प्रारूपण तक, AI हर कदम पर सहायता करता है',
        globalCoverage: 'वैश्विक कवरेज',
        globalCoverageDesc: '190+ देशों की कानूनी प्रणालियों तक पहुंच। सीमा-पार व्यापार के लिए एक-स्थान समाधान।',
        smartReview: 'स्मार्ट समीक्षा',
        smartReviewDesc: 'AI-संचालित अनुबंध विश्लेषण सेकंडों में जोखिमों की पहचान करता है',
        autoDrafting: 'स्वचालित प्रारूपण',
        autoDraftingDesc: 'स्वचालित रूप से पेशेवर कानूनी दस्तावेज़ तैयार करें',
        caseSearch: 'मामला खोज',
        caseSearchDesc: 'विशाल कानूनी डेटाबेस से बुद्धिमान मामला मिलान',
        enterpriseSecurity: 'एंटरप्राइज़ सुरक्षा',
        enterpriseSecurityDesc: 'एंड-टू-एंड एन्क्रिप्शन, SOC2 अनुपालक। आपके कानूनी दस्तावेज़ आपके रहते हैं।',
        multilingual: 'बहुभाषी',
        multilingualDesc: 'चीनी, अंग्रेजी, जापानी और अधिक में कानूनी परामर्श',
        coveringJurisdictions: 'प्रमुख न्यायाधिकार क्षेत्रों को कवर करता है',
        moreCountries: 'और 180+ अन्य देश',
        // Pricing Section
        pricingTitle: 'सरल और पारदर्शी मूल्य निर्धारण',
        pricingSubtitle: 'अपनी जरूरतों के अनुसार प्लान चुनें',
        planFreeDesc: 'बुनियादी कानूनी प्रश्न-उत्तर का अनुभव करें',
        planProDesc: 'व्यक्तिगत वकीलों के लिए स्मार्ट सहायक',
        planMaxDesc: 'निर्बाध वैश्विक कानूनी पहुंच',
        planUltraDesc: 'सीमा-पार रणनीतिक खुफिया',
        perMonth: '/माह',
        chatsPerDay: 'चैट/दिन',
        kbFiles: 'ज्ञान आधार फाइलें',
        singleCountryCoverage: 'एकल देश कवरेज',
        singleCountryDeep: 'एकल देश गहन कवरेज',
        globalCoveragePricing: 'वैश्विक कानूनी कवरेज',
        multiCountryAnalysis: 'बहु-देश विश्लेषण',
        aiReviewPerMonth: 'AI समीक्षा/माह',
        deepResearchQuota: 'गहन अनुसंधान',
        noAiReview: 'कोई AI समीक्षा/ड्राफ्ट नहीं',
        noDeepResearch: 'कोई गहन अनुसंधान नहीं',
        startUsing: 'शुरू करें',
        subscribeNow: 'अभी सदस्यता लें',
        enterpriseCustom: 'एंटरप्राइज़ समाधान चाहिए?',
        contactSales: 'बिक्री से संपर्क करें',
        readyToStart: 'शुरू करने के लिए तैयार हैं?',
        joinThousands: 'दुनिया भर के हजारों कानूनी पेशेवरों से जुड़ें',
        signUpFree: 'मुफ्त साइन अप करें',
        tagline: 'AI-संचालित कानूनी बुद्धिमत्ता',
        jurisdictionsLabel: 'न्यायाधिकार क्षेत्र',
        accuracyLabel: 'सटीकता',
        fasterLabel: 'तेज',
        availableLabel: 'उपलब्ध',
        platformCapabilities: 'प्लेटफ़ॉर्म क्षमताएं',
        testimonialQuote: '"LawHelper ने कानूनी कार्य को संभालने के हमारे तरीके को बदल दिया है। अनुबंध समीक्षा 10 गुना तेज है, अविश्वसनीय सटीकता के साथ।"',
        testimonialAuthor: 'कानूनी पेशेवर',
        testimonialPosition: 'शीर्ष कानूनी फर्म में भागीदार',
        viewPricing: 'मूल्य देखें',
        footerPricing: 'मूल्य',
        footerPrivacy: 'गोपनीयता',
        footerTerms: 'शर्तें',
        footerContact: 'संपर्क',
        footerCopyright: '© 2024 LawHelper. सर्वाधिकार सुरक्षित।',
    },

    'it': {
        getStarted: 'Inizia',
        badge: 'AI Legal Engine 2.0 rilasciato',
        heroTitle1: 'Legal AI',
        heroTitle2: 'Semplificato.',
        heroSubtitle: 'Il tuo assistente legale intelligente. Accedi a oltre 190 giurisdizioni in tutto il mondo. Dalla revisione dei contratti alla stesura dei documenti, rendi il lavoro legale complesso chiaro ed efficiente.',
        startFree: 'Inizia gratis',
        learnMore: 'Scopri di più',
        featuresTitle: 'Reimmagina il flusso di lavoro legale',
        featuresSubtitle: 'Dalla ricerca legale alla stesura dei documenti, l\'IA alimenta ogni passo',
        globalCoverage: 'Copertura globale',
        globalCoverageDesc: 'Accedi ai sistemi legali di oltre 190 paesi. Soluzione unica per affari transfrontalieri.',
        smartReview: 'Revisione intelligente',
        smartReviewDesc: 'L\'analisi dei contratti basata su IA identifica i rischi in pochi secondi',
        autoDrafting: 'Redazione automatica',
        autoDraftingDesc: 'Genera automaticamente documenti legali professionali',
        caseSearch: 'Ricerca casi',
        caseSearchDesc: 'Corrispondenza intelligente dei casi da vasti database legali',
        enterpriseSecurity: 'Sicurezza aziendale',
        enterpriseSecurityDesc: 'Crittografia end-to-end, conforme SOC2. I tuoi documenti legali restano tuoi.',
        multilingual: 'Multilingue',
        multilingualDesc: 'Consulenza legale in cinese, inglese, giapponese e altro',
        coveringJurisdictions: 'COPERTURA DELLE PRINCIPALI GIURISDIZIONI',
        moreCountries: 'e oltre 180 altri paesi',
        // Pricing Section
        pricingTitle: 'Prezzi semplici e trasparenti',
        pricingSubtitle: 'Scegli il piano adatto alle tue esigenze',
        planFreeDesc: 'Prova le domande e risposte legali di base',
        planProDesc: 'Assistente intelligente per avvocati singoli',
        planMaxDesc: 'Accesso legale globale senza interruzioni',
        planUltraDesc: 'Intelligence strategica transfrontaliera',
        perMonth: '/mese',
        chatsPerDay: 'chat/giorno',
        kbFiles: 'file della base di conoscenza',
        singleCountryCoverage: 'Copertura paese singolo',
        singleCountryDeep: 'Copertura approfondita paese singolo',
        globalCoveragePricing: 'Copertura legale globale',
        multiCountryAnalysis: 'Analisi multi-paese',
        aiReviewPerMonth: 'revisioni AI/mese',
        deepResearchQuota: 'Ricerca approfondita',
        noAiReview: 'Nessuna revisione/bozza AI',
        noDeepResearch: 'Nessuna ricerca approfondita',
        startUsing: 'Inizia',
        subscribeNow: 'Abbonati ora',
        enterpriseCustom: 'Hai bisogno di una soluzione enterprise?',
        contactSales: 'Contatta le vendite',
        readyToStart: 'Pronto per iniziare?',
        joinThousands: 'Unisciti a migliaia di professionisti legali in tutto il mondo',
        signUpFree: 'Registrati gratis',
        tagline: 'Intelligenza legale basata su IA',
        jurisdictionsLabel: 'Giurisdizioni',
        accuracyLabel: 'Precisione',
        fasterLabel: 'Più veloce',
        availableLabel: 'Disponibile',
        platformCapabilities: 'Funzionalità della piattaforma',
        testimonialQuote: '"LawHelper ha trasformato il modo in cui gestiamo il lavoro legale. La revisione dei contratti è 10 volte più veloce, con una precisione incredibile."',
        testimonialAuthor: 'Professionista legale',
        testimonialPosition: 'Partner presso studio legale leader',
        viewPricing: 'Vedi prezzi',
        footerPricing: 'Prezzi',
        footerPrivacy: 'Privacy',
        footerTerms: 'Termini',
        footerContact: 'Contatti',
        footerCopyright: '© 2024 LawHelper. Tutti i diritti riservati.',
    },

    'nl': {
        getStarted: 'Beginnen',
        badge: 'AI Legal Engine 2.0 uitgebracht',
        heroTitle1: 'Legal AI',
        heroTitle2: 'Vereenvoudigd.',
        heroSubtitle: 'Uw intelligente juridische assistent. Toegang tot 190+ jurisdicties wereldwijd. Van contractbeoordeling tot documentopstelling, maak complex juridisch werk duidelijk en efficiënt.',
        startFree: 'Gratis beginnen',
        learnMore: 'Meer informatie',
        featuresTitle: 'Herdefinieer de juridische workflow',
        featuresSubtitle: 'Van juridisch onderzoek tot documentopstelling, AI ondersteunt elke stap',
        globalCoverage: 'Wereldwijde dekking',
        globalCoverageDesc: 'Toegang tot rechtssystemen van 190+ landen. One-stop oplossing voor grensoverschrijdende zaken.',
        smartReview: 'Slimme beoordeling',
        smartReviewDesc: 'AI-gestuurde contractanalyse identificeert risico\'s in seconden',
        autoDrafting: 'Automatisch opstellen',
        autoDraftingDesc: 'Genereer automatisch professionele juridische documenten',
        caseSearch: 'Zaakzoeken',
        caseSearchDesc: 'Intelligente zaakmatching uit uitgebreide juridische databases',
        enterpriseSecurity: 'Enterprise beveiliging',
        enterpriseSecurityDesc: 'End-to-end encryptie, SOC2 compliant. Uw juridische documenten blijven van u.',
        multilingual: 'Meertalig',
        multilingualDesc: 'Juridisch advies in Chinees, Engels, Japans en meer',
        coveringJurisdictions: 'DEKKING VAN BELANGRIJKE JURISDICTIES',
        moreCountries: 'en 180+ andere landen',
        // Pricing Section
        pricingTitle: 'Eenvoudige, transparante prijzen',
        pricingSubtitle: 'Kies het plan dat bij u past',
        planFreeDesc: 'Ervaar basis juridische Q&A',
        planProDesc: 'Slimme assistent voor individuele advocaten',
        planMaxDesc: 'Naadloze wereldwijde juridische toegang',
        planUltraDesc: 'Grensoverschrijdende strategische intelligentie',
        perMonth: '/maand',
        chatsPerDay: 'chats/dag',
        kbFiles: 'kennisbank bestanden',
        singleCountryCoverage: 'Dekking enkel land',
        singleCountryDeep: 'Diepgaande dekking enkel land',
        globalCoveragePricing: 'Wereldwijde juridische dekking',
        multiCountryAnalysis: 'Multi-land analyse',
        aiReviewPerMonth: 'AI-reviews/maand',
        deepResearchQuota: 'Diepgaand onderzoek',
        noAiReview: 'Geen AI-review/concept',
        noDeepResearch: 'Geen diepgaand onderzoek',
        startUsing: 'Beginnen',
        subscribeNow: 'Nu abonneren',
        enterpriseCustom: 'Enterprise-oplossing nodig?',
        contactSales: 'Contact verkoop',
        readyToStart: 'Klaar om te beginnen?',
        joinThousands: 'Sluit u aan bij duizenden juridische professionals wereldwijd',
        signUpFree: 'Gratis registreren',
        tagline: 'AI-aangedreven juridische intelligentie',
        jurisdictionsLabel: 'Rechtsgebieden',
        accuracyLabel: 'Nauwkeurigheid',
        fasterLabel: 'Sneller',
        availableLabel: 'Beschikbaar',
        platformCapabilities: 'Platformmogelijkheden',
        testimonialQuote: '"LawHelper heeft onze juridische werkwijze getransformeerd. Contractbeoordeling is 10x sneller, met ongelooflijke nauwkeurigheid."',
        testimonialAuthor: 'Juridisch professional',
        testimonialPosition: 'Partner bij toonaangevend kantoor',
        viewPricing: 'Bekijk prijzen',
        footerPricing: 'Prijzen',
        footerPrivacy: 'Privacy',
        footerTerms: 'Voorwaarden',
        footerContact: 'Contact',
        footerCopyright: '© 2024 LawHelper. Alle rechten voorbehouden.',
    },

    'tr': {
        getStarted: 'Başla',
        badge: 'AI Legal Engine 2.0 yayınlandı',
        heroTitle1: 'Legal AI',
        heroTitle2: 'Basitleştirildi.',
        heroSubtitle: 'Akıllı hukuki asistanınız. Dünya genelinde 190+ yargı yetkisine erişin. Sözleşme incelemesinden belge hazırlamaya kadar, karmaşık hukuki çalışmayı net ve verimli hale getirin.',
        startFree: 'Ücretsiz başla',
        learnMore: 'Daha fazla bilgi',
        featuresTitle: 'Hukuki iş akışını yeniden tasarlayın',
        featuresSubtitle: 'Hukuki araştırmadan belge hazırlamaya, AI her adımda destek sağlar',
        globalCoverage: 'Küresel kapsama',
        globalCoverageDesc: '190+ ülkenin hukuk sistemlerine erişin. Sınır ötesi iş için tek noktadan çözüm.',
        smartReview: 'Akıllı inceleme',
        smartReviewDesc: 'AI destekli sözleşme analizi riskleri saniyeler içinde tespit eder',
        autoDrafting: 'Otomatik taslak',
        autoDraftingDesc: 'Profesyonel hukuki belgeleri otomatik olarak oluşturun',
        caseSearch: 'Dava arama',
        caseSearchDesc: 'Geniş hukuki veritabanlarından akıllı dava eşleştirme',
        enterpriseSecurity: 'Kurumsal güvenlik',
        enterpriseSecurityDesc: 'Uçtan uca şifreleme, SOC2 uyumlu. Hukuki belgeleriniz sizin kalır.',
        multilingual: 'Çok dilli',
        multilingualDesc: 'Çince, İngilizce, Japonca ve daha fazlasında hukuki danışmanlık',
        coveringJurisdictions: 'ANA YARGI YETKİLERİNİ KAPSAR',
        moreCountries: 've 180+ diğer ülke',
        // Pricing Section
        pricingTitle: 'Basit ve şeffaf fiyatlandırma',
        pricingSubtitle: 'İhtiyaçlarınıza uygun planı seçin',
        planFreeDesc: 'Temel hukuki soru-cevapları deneyimleyin',
        planProDesc: 'Bireysel avukatlar için akıllı asistan',
        planMaxDesc: 'Kesintisiz küresel hukuki erişim',
        planUltraDesc: 'Sınır ötesi stratejik istihbarat',
        perMonth: '/ay',
        chatsPerDay: 'sohbet/gün',
        kbFiles: 'bilgi tabanı dosyası',
        singleCountryCoverage: 'Tek ülke kapsama',
        singleCountryDeep: 'Tek ülke derin kapsama',
        globalCoveragePricing: 'Küresel hukuki kapsama',
        multiCountryAnalysis: 'Çok ülkeli analiz',
        aiReviewPerMonth: 'AI inceleme/ay',
        deepResearchQuota: 'Derin araştırma',
        noAiReview: 'AI inceleme/taslak yok',
        noDeepResearch: 'Derin araştırma yok',
        startUsing: 'Başla',
        subscribeNow: 'Şimdi abone ol',
        enterpriseCustom: 'Kurumsal çözüme mi ihtiyacınız var?',
        contactSales: 'Satışa başvur',
        readyToStart: 'Başlamaya hazır mısınız?',
        joinThousands: 'Dünya çapında binlerce hukuk uzmanına katılın',
        signUpFree: 'Ücretsiz kaydol',
        tagline: 'AI destekli hukuki zeka',
        jurisdictionsLabel: 'Yargı bölgeleri',
        accuracyLabel: 'Doğruluk',
        fasterLabel: 'Daha hızlı',
        availableLabel: 'Mevcut',
        platformCapabilities: 'Platform yetenekleri',
        testimonialQuote: '"LawHelper hukuki çalışma şeklimizi tamamen değiştirdi. Sözleşme incelemesi 10 kat daha hızlı, inanılmaz doğrulukla."',
        testimonialAuthor: 'Hukuk uzmanı',
        testimonialPosition: 'Önde gelen hukuk firmasında ortak',
        viewPricing: 'Fiyatları gör',
        footerPricing: 'Fiyatlar',
        footerPrivacy: 'Gizlilik',
        footerTerms: 'Şartlar',
        footerContact: 'İletişim',
        footerCopyright: '© 2024 LawHelper. Tüm hakları saklıdır.',
    },
};

// Get translation for a specific language, fallback to English
export const getLandingTranslation = (langCode: string): LandingTranslation => {
    return landingTranslations[langCode] || landingTranslations['en-US'];
};
